import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { getPreferences } from '../../../controllers/preferences-controls/get-preferences';
import { CachedKeysDataProperty, PreferenceCategory } from '../../../types';
import { loadStorageControlsData } from '../load-storage-controls-data';
export var findStartsWithPatternMatch = function findStartsWithPatternMatch(itemKey, _ref) {
  var startsWith = _ref.startsWith;
  // Setup initial pattern step and position
  var currentStep = startsWith;
  var position = 0;

  // Iterate through item key symbol by symbol
  while (position < itemKey.length) {
    // The value of item character at the position
    var currentStepValue = currentStep[itemKey[position]];
    // If there is no match for item symbol in cache, this means there is no
    // pattern match for the item key
    if (currentStepValue === undefined) {
      return null;
    }
    // If current step value is a number, this means we reached the end of the pattern
    // and should return category number
    if (typeof currentStepValue === 'number') {
      return currentStepValue;
    }
    // Continue to going through pattern
    currentStep = currentStepValue;
    position++;
  }

  // No pattern match - all patterns longer or shorter than item key
  return null;
};

/**
 * Synchronously fetches a an item/cookie category from a CategorizedStorageControlsCache that has already been fetched
 *
 * This allows synchronous parts of the code to access cached versions of the cache but still maintain
 * a synchronous signature for clients
 *
 */
export var getItemCategoryFromPassedCache = function getItemCategoryFromPassedCache(itemKey, storageControlsData, cachedKeysDataProperty) {
  var _numToCategoryMap;
  var itemCategoryNumber = null;
  var categories = storageControlsData.categories,
    patterns = storageControlsData.patterns;
  var keys = storageControlsData[cachedKeysDataProperty];

  // Find direct match in keys
  if (itemKey in Object(keys)) {
    itemCategoryNumber = keys[itemKey];
  } else {
    // Find pattern match or null
    itemCategoryNumber = findStartsWithPatternMatch(itemKey, patterns);
  }
  var numToCategoryMap = (_numToCategoryMap = {}, _defineProperty(_numToCategoryMap, categories.STRICTLY_NECESSARY, PreferenceCategory.StrictlyNecessary), _defineProperty(_numToCategoryMap, categories.FUNCTIONAL, PreferenceCategory.Functional), _defineProperty(_numToCategoryMap, categories.ANALYTICS, PreferenceCategory.Analytics), _defineProperty(_numToCategoryMap, categories.MARKETING, PreferenceCategory.Marketing), _defineProperty(_numToCategoryMap, categories.UNKNOWN, PreferenceCategory.Unknown), _numToCategoryMap);
  return itemCategoryNumber !== null ? numToCategoryMap[itemCategoryNumber] : null;
};
export var getItemCategory = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(itemKey, cachedKeysDataProperty) {
    var storageControlsData;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _context.next = 2;
          return loadStorageControlsData();
        case 2:
          storageControlsData = _context.sent;
          if (storageControlsData) {
            _context.next = 7;
            break;
          }
          return _context.abrupt("return", null);
        case 7:
          return _context.abrupt("return", getItemCategoryFromPassedCache(itemKey, storageControlsData, cachedKeysDataProperty));
        case 8:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function getItemCategory(_x, _x2) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * Given an item key (WebStorage key or cookie name), fetches preferences and categories and determines whether the key can be set
 *
 * Allows an optional allowedCallback to be passed which is invoked in the case the cookie is allowed to be set.
 * This allows synchronous parts of the code to check a item's status (e.g. the localStorage or document.cookie overrides)
 *
 */
export var isProcessingAllowedByPreferences = /*#__PURE__*/function () {
  var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(_ref3) {
    var itemKey, _ref3$allowedCallback, allowedCallback, _ref3$blockedCallback, blockedCallback, _ref3$cachedKeysDataP, cachedKeysDataProperty, shouldProcessItem, shouldSkip, itemCategory, preferences, hasPreferencesForCategory;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          itemKey = _ref3.itemKey, _ref3$allowedCallback = _ref3.allowedCallback, allowedCallback = _ref3$allowedCallback === void 0 ? function () {} : _ref3$allowedCallback, _ref3$blockedCallback = _ref3.blockedCallback, blockedCallback = _ref3$blockedCallback === void 0 ? function () {} : _ref3$blockedCallback, _ref3$cachedKeysDataP = _ref3.cachedKeysDataProperty, cachedKeysDataProperty = _ref3$cachedKeysDataP === void 0 ? CachedKeysDataProperty.Cookies : _ref3$cachedKeysDataP;
          // Skipping this because storageControlsData does not have actual web storage data
          // TODO: JELLY-808: Remove this skip once we have the cached LS/SS data in our categories.json
          shouldSkip = cachedKeysDataProperty === CachedKeysDataProperty.LocalStorage || cachedKeysDataProperty === CachedKeysDataProperty.SessionStorage; // TODO: Remove this check when categories.json returns the proper keys
          if (!shouldSkip) {
            _context2.next = 6;
            break;
          }
          _context2.t0 = PreferenceCategory.StrictlyNecessary;
          _context2.next = 9;
          break;
        case 6:
          _context2.next = 8;
          return getItemCategory(itemKey, cachedKeysDataProperty);
        case 8:
          _context2.t0 = _context2.sent;
        case 9:
          itemCategory = _context2.t0;
          if (!(itemCategory === PreferenceCategory.StrictlyNecessary)) {
            _context2.next = 14;
            break;
          }
          shouldProcessItem = true;
          _context2.next = 20;
          break;
        case 14:
          _context2.next = 16;
          return getPreferences();
        case 16:
          preferences = _context2.sent;
          hasPreferencesForCategory = false;
          if (!!preferences) {
            if (!!itemCategory) {
              hasPreferencesForCategory = preferences[itemCategory];
            }
          }
          shouldProcessItem = hasPreferencesForCategory;
        case 20:
          if (shouldProcessItem) {
            allowedCallback === null || allowedCallback === void 0 || allowedCallback();
          } else {
            blockedCallback === null || blockedCallback === void 0 || blockedCallback({
              itemHasCategory: itemCategory != null
            });
          }
          return _context2.abrupt("return", shouldProcessItem);
        case 22:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return function isProcessingAllowedByPreferences(_x3) {
    return _ref4.apply(this, arguments);
  };
}();